import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isRtlLang } from '../i18n';

type Props = {
  compact?: boolean;
  hideTitle?: boolean;
};

const ContactUs: React.FC<Props> = ({ compact = false, hideTitle = false }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    setIsRTL(isRtlLang(i18n.language));
  }, [i18n.language]);

  const countries =
    (t('contact.country', { returnObjects: true }) as Record<string, any>) ||
    {};

  const refinePhone = (phone: string) => {
    if (!phone) return '';
    // keep digits only for wa.me
    return phone.replace(/\+|\s|-/g, '');
  };

  const countryKeys = Object.keys(countries || {});

  return (
    <div className={compact ? 'space-y-4' : 'space-y-8'}>
      <div className={compact ? 'space-y-2' : 'space-y-8'}>
        {countryKeys.map((countryKey) => {
          const country = countries[countryKey] as Record<string, any>;
          const countryName = t(`contact.country.${countryKey}.info.name`);
          const countryFlag = t(`contact.country.${countryKey}.info.flag`);
          const cityKeys = country
            ? Object.keys(country).filter((k) => k !== 'info' && k !== 'name')
            : [];

          return (
            <div
              key={countryKey}
              className={compact ? 'space-y-1' : 'space-y-4'}
            >
              <div className="flex items-center">
                <i
                  className={`fi ${countryFlag} ${
                    compact ? 'text-sm' : 'text-base md:text-lg'
                  } ${isRTL ? 'ml-2' : 'mr-2'}
                  `}
                ></i>
                <h3
                  className={`font-semibold ${compact ? 'text-sm' : 'text-lg'}`}
                >
                  {countryName}
                </h3>
              </div>

              {compact ? (
                <div className="pl-4">
                  {cityKeys.map((cityKey) => {
                    const cityPath = `contact.country.${countryKey}.${cityKey}`;
                    const phone = t(`${cityPath}.phone`);
                    const wa = refinePhone(phone);

                    return (
                      <div
                        key={cityKey}
                        className="flex flex-col md:flex-row items-start md:items-center py-1"
                      >
                        <div className="w-full md:w-[140px] flex items-center md:mx-4">
                          <i className="fas fa-map-marker-alt text-blue-600 mx-2 text-sm md:text-base"></i>
                          <span className="font-semibold text-sm md:text-base">
                            {t(`${cityPath}.name`)}
                          </span>
                        </div>

                        <div className="flex items-center mt-1 md:mt-0">
                          <i className="fas fa-phone-alt text-blue-600 phone-ring mx-2 text-sm md:text-base"></i>
                          <a
                            href={wa ? `https://wa.me/${wa}` : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ direction: 'ltr' }}
                            className="text-sm md:text-base break-words"
                          >
                            {phone}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                cityKeys.map((cityKey) => {
                  const cityPath = `contact.country.${countryKey}.${cityKey}`;
                  const phone = t(`${cityPath}.phone`);
                  const wa = refinePhone(phone);

                  return (
                    <div key={cityKey}>
                      <div
                        className={`flex items-center gap-2 ${
                          isRTL ? 'lg:mr-8' : 'lg:ml-8'
                        }`}
                      >
                        <i className="fas fa-map-marker-alt text-blue-600 text-lg"></i>
                        <span className="font-semibold">
                          {t(`${cityPath}.name`)}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-2 ${
                          isRTL ? 'lg:mr-8' : 'lg:ml-8'
                        }`}
                      >
                        <i className="fas fa-phone-alt text-blue-600 text-lg phone-ring"></i>
                        <a
                          href={wa ? `https://wa.me/${wa}` : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mx-1 flex"
                          style={{ direction: 'ltr' }}
                        >
                          {phone}
                        </a>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}

        <div className="flex items-center">
          <i className="fas fa-envelope text-blue-600 mx-3 text-xl"></i>
          <div>
            {!compact && (
              <h3 className="text-lg font-semibold text-gray-900">
                {t('contact.formEmail')}
              </h3>
            )}
            <p>{t('contact.email')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
